import { observer } from 'mobx-react';
import moment from 'moment';
import { useCallback, useMemo } from 'react';
import Calendar, { FormatterCallback, OnChangeDateRangeCallback } from 'react-calendar';
import FilterButton from '../../FilterButton';
import { useAdvancedFilterContext } from '../../context';
import { AdvancedFilterProperties } from '../../types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { LOCALES } from 'src/constants/main';
import { localeStore } from 'src/mobx/localesStore';
import Icon from 'src/view/components/Controls/Icon/Icon';
import { DateRanges, defaultDate, defaultDateFormat, presetDatesDataMap } from '~/constants/dateTime';
import './styles.scss';

interface DateFilterModalProps {
  onClose: () => void;
  // property: AdvancedFilterProperties;
}

const formatMonth: FormatterCallback = (locale, date) => moment(date).format('MMM');
const formatShortWeekday: FormatterCallback = (locale, date) => moment(date).format('dd');

const DateFilterModal = ({ onClose }: DateFilterModalProps) => {
  const { store } = useAdvancedFilterContext();

  const { maxDate, minDate } = useMemo(
    () => ({
      maxDate: store.allowDatesInTheFuture ? moment().add(90, 'days').toDate() : moment().endOf('day').toDate(),
      minDate: new Date(2017, 0, 1),
    }),
    [store.allowDatesInTheFuture]
  );

  const handleChangeDate: OnChangeDateRangeCallback = useCallback(
    ([fromDate, toDate]) => {
      if (fromDate && toDate) {
        store.selectRange(AdvancedFilterProperties.Date, { from: fromDate.getTime(), to: toDate.getTime() });
      }
    },
    [store]
  );

  const handleUsePreset = useCallback(
    (preset: DateRanges) => {
      const range = presetDatesDataMap[preset].getRange();
      store.selectRange(AdvancedFilterProperties.Date, { preset, ...range });
    },
    [store]
  );

  const _preset = store.selectedRanges[AdvancedFilterProperties.Date].get('preset');
  const _range = typeof _preset === 'number' ? presetDatesDataMap[_preset as DateRanges].getRange() : null;

  const timestampFrom = _range === null ? store.selectedRanges[AdvancedFilterProperties.Date].get('from') : _range.from;
  const formattedDateFrom = timestampFrom ? moment(timestampFrom).format(defaultDateFormat) : defaultDate;

  const timestampTo = _range === null ? store.selectedRanges[AdvancedFilterProperties.Date].get('to') : _range.to;
  const formattedDateTo = timestampTo ? moment(timestampTo).format(defaultDateFormat) : defaultDate;

  const value: Date | [Date, Date] | null | undefined =
    timestampFrom && timestampTo
      ? [new Date(timestampFrom), new Date(timestampTo)]
      : timestampFrom === null && timestampTo === null
      ? null
      : undefined;

  return (
    <div className="advanced-filter-date-modal">
      <div className="px-8 py-5">
        <h3 className="family-bold color-dark text-size-26 flex-1">{localeStore.t('date_range')}</h3>
      </div>
      <div className="close-icon" onClick={onClose}>
        <Icon name={Icons.Close} size={10} />
      </div>
      <div className="d-flex flex-1 px-8 pb-4 content">
        <div className="preset-dates-list">
          {store.presetDates.map(({ preset: __preset, title }, i) => (
            <div key={i} className="preset-date">
              <div onClick={() => handleUsePreset(__preset)}>{localeStore.t(title)}</div>
              {__preset === store.selectedRanges[AdvancedFilterProperties.Date].get('preset') ? <div className="preset-date-selected" /> : null}
            </div>
          ))}
        </div>
        <div className="calendar-wrapper">
          <div className="d-flex flex-row justify-content-between mx-3 mt-2">
            <div className="d-flex flex-column align-items-center color-dark ">
              <h2 className="family-medium text-size-12 text-uppercase letter-spacing-12">{localeStore.t('start_date')}</h2>
              <p className="family-medium text-size-18 mt-1">{formattedDateFrom}</p>
            </div>
            <div className="d-flex flex-column align-items-center color-dark ">
              <h2 className="family-medium text-size-12 text-uppercase letter-spacing-12">{localeStore.t('end_date')}</h2>
              <p className="family-medium text-size-18 mt-1">{formattedDateTo}</p>
            </div>
          </div>
          <Calendar
            maxDate={maxDate}
            minDate={minDate}
            className="mt-4"
            minDetail="decade"
            calendarType="US"
            value={value}
            onChange={handleChangeDate}
            locale={localeStore.language}
            formatMonth={localeStore.language === LOCALES.AR ? undefined : formatMonth}
            formatShortWeekday={localeStore.language === LOCALES.AR ? undefined : formatShortWeekday}
            prevLabel={<Icon name={Icons.SliderLeft} className="pagination__button-arrow" />}
            nextLabel={<Icon name={Icons.SliderRight} className="pagination__button-arrow" />}
            selectRange
          />
        </div>
      </div>
      <div className="advanced-filter-buttons">
        <FilterButton title="apply" onClick={onClose} variant="primary" />
        <FilterButton title="txt_clear" onClick={() => store.resetPropertyValues(AdvancedFilterProperties.Date)} />
      </div>
    </div>
  );
};

export default observer(DateFilterModal);
